import {
    UrlSubdirectoryTypes,
    LocationType,
    ResultType,
    selectResultType,
    selectLocationType
} from "features/searchResultsFacets";
import PageType from 'enums/pageType';
import { store } from '@redux/store';

function getTrackerName() {
  const nhsTrackingId = 'UA-40843711-4';
  const trackers = window.ga.getAll().filter((element) => element.get('trackingId') === nhsTrackingId);
  const tracker = trackers[0].get('name');
  return tracker;
}

function getTrackerClientId() {
  const nhsTrackingId = 'UA-40843711-4';
  const trackers = window.ga.getAll().filter((element) => element.get('trackingId') === nhsTrackingId);
  const clientId = trackers[0].get('clientId');
  return clientId
}

/**
 * Returns the currently active result type (active tab) from Redux state.
 * @param {object} state - Redux state.
 * @returns {string|undefined} The active result type key.
 */
const getActiveResultType = (state) => selectResultType(state || store.getState());

/**
 * A function that returns the value of the page type to be tracked in Google Analytics custom dimensions.
 * For instance, in Homes searches it will return `Home_Results`; in Single Family cases `Single_Family_Homes`.
 * For pages that support tab switching (communities/homes), it checks the active result type in Redux.
 * For state pages, it returns the appropriate page type based on the result type.
 * @param {string} page - The page subdirectory key.
 * @param {string} locationType - Optional location type to determine if it's a state page.
 * @returns {string} The page type string.
 */
const getPageType = (page, locationType) => {
  // Get locationType and resultType from Redux store if not provided
  const state = locationType ? null : store.getState();
  const currentLocationType = locationType || selectLocationType(state);
  const currentResultType = getActiveResultType(state);

  // Check if this is a state page by checking locationType first
  if (currentLocationType === LocationType.State.toString()) {
    const resultTypeFromUrl = ResultType.fromKey(page)?.toString();
    const resultType = currentResultType || resultTypeFromUrl;

    if (resultType === ResultType.Communities.toString()) {
      return PageType.CommunitiesState.toString();
    }

    if (resultType === ResultType.Homes.toString()) {
      return PageType.HomesState.toString();
    }

    // Default to Communities State if resultType is not yet set
    // (happens on initial page load before Redux is fully hydrated)
    return page === UrlSubdirectoryTypes.Homes.toString()
      ? PageType.HomesState.toString()
      : PageType.CommunitiesState.toString();
  }

  // For pages with communities/homes subdirectory that support tab switching,
  // check the active result type to determine the correct page type
  if (page === UrlSubdirectoryTypes.Communities.toString() || page === UrlSubdirectoryTypes.Homes.toString()) {
    if (currentResultType === ResultType.Homes.toString()) {
      return PageType.Homes.toString();
    }
    if (currentResultType === ResultType.Communities.toString()) {
      return PageType.Communities.toString();
    }
  }

  // For all other pages, use the existing logic based on subdirectory
  return UrlSubdirectoryTypes.fromKey(page)?.pageType || '';
};

export { getTrackerName, getPageType, getTrackerClientId };
